import React from "react";
import Checkbox from "@mui/material/Checkbox";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { toast } from "react-toastify";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import RightworkDatePicker from "../../../components/shared/RightworkDatePicker";
import RightworkTimePicker from "../../../components/shared/RightworkTimePicker";
import ConfirmDialog from "../boards/ConfirmDialog";
import LoadSpinner from "../../../utilities/LoadSpinner";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_archive_tip_policy,
  API_assign_tip_policy_stores,
  API_save_tip_policy,
  API_save_tip_settings,
  API_unassign_tip_policy_store,
} from "../../../constants";

// Settings > Tip pools. A policy is written once (pools, cadence, windows,
// sources, points per job code) and assigned to locations. The parent page
// fetches the payload and refetches after each save through `onChanged`.
// Editing happens in a right-hand drawer, like Store groups.

const CADENCES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "pay_period", label: "Pay period" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SOURCE_SHORT = {
  credit_card_tip: "Credit card tips",
  gift_card_tip: "Gift card tips",
  gratuity_service_charge: "Gratuity service charges",
  declared_cash_tip: "Declared cash",
  online_order_tip: "Online ordering tips",
};

// The browser's local calendar date. toISOString() would give the UTC date,
// which after 5pm Pacific is already tomorrow.
const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
const byName = (a, b) => (a.name || a.store_name || a.role_name || "").localeCompare(b.name || b.store_name || b.role_name || "");

const parseIsoDate = (text) => { const [y, m, d] = String(text).split("-").map(Number); return new Date(y, m - 1, d); };
const isoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const draftKey = (companyId) => `tipPoolsEditor:${companyId}`;
const readDraft = (companyId) => {
  try {
    const raw = sessionStorage.getItem(draftKey(companyId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const writeDraft = (companyId, editor) => {
  try {
    if (editor) sessionStorage.setItem(draftKey(companyId), JSON.stringify(editor));
    else sessionStorage.removeItem(draftKey(companyId));
  } catch (_) {
    // Storage can be unavailable; the draft is a convenience, not the record.
  }
};
const snapshot = (editor) => JSON.stringify({ name: editor.name, notes: editor.notes, pools: editor.pools });
const withSnapshot = (editor) => ({ ...editor, initial: snapshot(editor) });
const isDirty = (editor) => Boolean(editor) && snapshot(editor) !== editor.initial;

const emptyWindows = () => DAYS.map((_, day) => ({ day_of_week: day, enabled: day < 5, start_time: "16:00", end_time: "04:00" }));
const newPool = (index) => ({
  pool_uuid: null,
  name: index === 0 ? "House pool" : `Pool ${index + 1}`,
  cadence: "daily",
  always_active: true,
  windows: emptyWindows(),
  sources: ["credit_card_tip", "gift_card_tip", "gratuity_service_charge"],
  excluded_dining_options: [],
  share_percent: "100",
  split_group: "",
  role_points: {},
});
// Server pools carry windows only when active; the editor keeps a row per day.
const poolToEditor = (pool) => {
  const rows = emptyWindows().map((row) => ({ ...row, enabled: false }));
  (pool.windows || []).forEach((window) => {
    const row = rows[window.day_of_week];
    if (row) Object.assign(row, { enabled: true, start_time: window.start_time, end_time: window.end_time });
  });
  // This editor shows one window per weekday. A pool built with several
  // windows on the same day (a lunch and a dinner window) keeps them
  // exactly as they are: they are shown read-only and sent back untouched.
  const perDay = {};
  (pool.windows || []).forEach((window) => { perDay[window.day_of_week] = (perDay[window.day_of_week] || 0) + 1; });
  const windowsLocked = Object.values(perDay).some((count) => count > 1);
  const points = {};
  (pool.role_points || []).forEach((item) => { points[item.role_id] = String(item.points).replace(/\.00$/, ""); });
  return {
    pool_uuid: pool.uuid,
    name: pool.name,
    cadence: pool.cadence,
    always_active: pool.always_active,
    windows: rows,
    windows_locked: windowsLocked,
    raw_windows: windowsLocked ? (pool.windows || []).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time })) : null,
    sources: pool.sources || [],
    excluded_dining_options: pool.excluded_dining_options || [],
    share_percent: String(pool.share_percent).replace(/\.00$/, ""),
    split_group: pool.split_group || "",
    role_points: points,
  };
};
const editorToPayload = (editor) => ({
  policy_uuid: editor.uuid,
  // The version this editor was opened from; the server refuses the save if
  // the policy changed since, instead of overwriting someone else's edits.
  ...(editor.uuid && editor.serverUpdatedAt ? { expected_updated_at: editor.serverUpdatedAt } : {}),
  name: editor.name,
  notes: editor.notes,
  pools: editor.pools.map((pool) => ({
    pool_uuid: pool.pool_uuid,
    name: pool.name,
    cadence: pool.cadence,
    always_active: pool.always_active,
    windows: pool.always_active ? [] : pool.windows_locked && pool.raw_windows ? pool.raw_windows : pool.windows.filter((row) => row.enabled).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time })),
    sources: pool.sources,
    excluded_dining_options: pool.excluded_dining_options,
    share_percent: pool.share_percent,
    split_group: pool.split_group,
    role_points: Object.entries(pool.role_points).map(([role_id, points]) => ({ role_id: Number(role_id), points })),
  })),
});

const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  padding: "5px",
  height: "45px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};
const smallInputSx = { ...inputSx, height: "36px", padding: "2px", fontSize: "13px" };
const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 };

const windowSummary = (pool) => {
  if (pool.always_active || !pool.windows?.length) return "Always active";
  const byRange = {};
  pool.windows.forEach((window) => {
    const key = `${window.start_time}–${window.end_time}`;
    byRange[key] = [...(byRange[key] || []), DAYS[window.day_of_week]];
  });
  return Object.entries(byRange).map(([range, days]) => `${days.join(" ")} ${range}`).join(" · ");
};

export default function TipPoolsDisplay({ companyId, payload, isAdmin, onChanged, onDirtyChange }) {
  const policies = React.useMemo(() => (payload?.policies || []).slice(), [payload]);
  const stores = React.useMemo(() => (payload?.stores || []).slice().sort(byName), [payload]);
  const roles = React.useMemo(() => (payload?.roles || []).slice().sort(byName), [payload]);
  const roleName = (roleId) => roles.find((role) => role.id === Number(roleId))?.role_name || `Role ${roleId}`;
  const active = policies.filter((policy) => policy.status === "active");
  const archived = policies.filter((policy) => policy.status !== "active");
  const today = todayIso();
  const storeById = (id) => stores.find((store) => Number(store.id) === Number(id));
  // Locations on a policy come from its effective-dated assignments, so a move
  // scheduled for a future date is visible (and cancellable) before it starts.
  // The server labels every assignment current / scheduled / ended by the
  // location's own business date; nothing here compares dates itself.
  const assignmentsOn = (policyUuid) => {
    const policy = policies.find((item) => item.uuid === policyUuid);
    const rows = (policy?.assignments || [])
      .filter((assignment) => assignment.status !== "ended")
      .map((assignment) => ({ ...assignment, store: storeById(assignment.store_id) }))
      .filter((assignment) => assignment.store);
    // A location whose current pointer names the policy but has no matching
    // record (older data) still shows, without dates.
    stores.filter((store) => store.current_policy_uuid === policyUuid && !rows.some((row) => row.store_id === store.id))
      .forEach((store) => rows.push({ uuid: `current-${store.id}`, store_id: store.id, effective_start: store.current_effective_start, effective_end: null, status: "current", store }));
    return rows.sort((a, b) => byName(a.store, b.store));
  };
  // Each location once, for counts: a location on the policy now with a
  // scheduled return to it later has two assignment rows but is one location.
  const storesOn = (policyUuid) => Array.from(new Map(assignmentsOn(policyUuid).map((row) => [row.store_id, row.store])).values());
  // The chronologically next scheduled move, whatever policy it belongs to.
  // "Current" is decided by the server per location (its own business
  // date); a scheduled move is any live assignment that is not the current
  // one and starts after it.
  const isScheduled = (assignment) => assignment.status === "scheduled";
  const allAssignments = () => policies.flatMap((policy) => (policy.assignments || []).map((assignment) => ({ ...assignment, policy_uuid: assignment.policy_uuid || policy.uuid, policy })));
  const upcomingFor = (storeId) => allAssignments()
    .filter((assignment) => assignment.store_id === storeId && isScheduled(assignment))
    .sort((a, b) => a.effective_start.localeCompare(b.effective_start))[0];
  const hasCurrent = (storeId) => allAssignments().some((assignment) => assignment.store_id === storeId && assignment.status === "current");
  // The earliest start a location may be assigned from: today when it is on
  // a policy now, the day after its last assignment ended when it had one
  // before (that history may be confirmed and is never rewritten), and any
  // date when it has never had one. The server enforces the same rule.
  const earliestStartFor = (storeId) => {
    // The server refuses a start on or before the start of any live
    // assignment (current or scheduled), so the floor is the day after the
    // latest of those starts: tomorrow when the current one began on the
    // location's own business date today, otherwise today; after a
    // scheduled move, the day after it.
    const live = allAssignments().filter((assignment) => assignment.store_id === storeId && (assignment.status === "current" || isScheduled(assignment)));
    if (live.length > 0) {
      // The location's own business date (from the server), not the browser's.
      let floor = storeById(storeId)?.today || today;
      const latestStart = live.map((assignment) => assignment.effective_start).filter(Boolean).sort().pop();
      if (latestStart) {
        const next = parseIsoDate(latestStart);
        next.setDate(next.getDate() + 1);
        const dayAfter = isoDate(next);
        if (dayAfter > floor) floor = dayAfter;
      }
      return floor;
    }
    const ended = allAssignments()
      .filter((assignment) => assignment.store_id === storeId && assignment.status === "ended" && assignment.effective_end)
      .map((assignment) => assignment.effective_end)
      .sort()
      .pop();
    if (!ended) return undefined;
    const next = parseIsoDate(ended);
    next.setDate(next.getDate() + 1);
    return isoDate(next);
  };
  const earliestStartForAll = (storeIds) => storeIds.map(earliestStartFor).filter(Boolean).sort().pop();
  // Tip pooling reads Toast orders and punches; other locations cannot be assigned yet.
  // ...and the location's tip pooling switch must be on, or the nightly
  // rebuild never visits it (the server refuses the assignment otherwise).
  // ...and the server must know the location's time zone (from its
  // coordinates), or every rebuild there would fail before writing a run.
  const zoneKnown = (store) => store.time_zone_known !== false;
  const assignable = stores.filter((store) => store.pos_provider === "toast" && store.tip_pooling_enabled && zoneKnown(store));
  const unassigned = assignable.filter((store) => !hasCurrent(store.id) && !upcomingFor(store.id));

  const [selectedUuid, setSelectedUuid] = React.useState(null);
  const selected = active.find((policy) => policy.uuid === selectedUuid) || archived.find((policy) => policy.uuid === selectedUuid) || active[0] || null;
  const [editor, setEditor] = React.useState(() => readDraft(companyId));
  const [assigning, setAssigning] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // The names drawer works on the backend's checklist: every service charge
  // name Toast has sent this company plus any saved name it has not, each
  // keyed by the canonical form the ledger matches on. Picks are entries of
  // that list, in the order they were saved or added.
  const [settingsPicked, setSettingsPicked] = React.useState([]);
  const [customName, setCustomName] = React.useState("");
  const checklist = payload?.service_charge_checklist || [];
  const savedEntries = checklist.filter((row) => row.saved);
  // Only a name typed by hand needs a key made here; a typed name that is
  // really a variant of a listed one is folded in by the server on save and
  // comes back as that row.
  const typedName = customName.replace(/\s+/g, " ").trim();
  const typedEntry = typedName ? { key: typedName.toLowerCase(), name: typedName } : null;
  const isPicked = (key) => settingsPicked.some((picked) => picked.key === key);
  const namesToSave = typedEntry && !isPicked(typedEntry.key) ? [...settingsPicked, typedEntry] : settingsPicked;
  const sameKeys = (a, b) => JSON.stringify(a.map((entry) => entry.key).sort()) === JSON.stringify(b.map((entry) => entry.key).sort());
  const settingsDirty = !sameKeys(namesToSave, savedEntries);
  const nameRows = [...checklist, ...settingsPicked.filter((picked) => !checklist.some((row) => row.key === picked.key)).map((picked) => ({ ...picked, seen: false }))];
  const togglePicked = (entry) => setSettingsPicked((prev) => (isPicked(entry.key) ? prev.filter((picked) => picked.key !== entry.key) : [...prev, { key: entry.key, name: entry.name }]));
  const addCustomName = () => {
    if (!typedEntry) return;
    if (!isPicked(typedEntry.key)) setSettingsPicked((prev) => [...prev, typedEntry]);
    setCustomName("");
  };
  const [pendingArchive, setPendingArchive] = React.useState(null);
  const [pendingUnassign, setPendingUnassign] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // Unsaved edits in either drawer go to the settings page's dirty guard, so
  // switching tab or company asks before this view unmounts and loses them.
  // The names have no session draft, unlike the policy editor.
  const assigningDirty = Boolean(assigning) && (assigning.store_ids.length > 0 || assigning.effective_start !== assigning.initial_effective_start);
  // The policy editor is not counted here: its draft is written to session
  // storage on every change and reopens on return, so moving around the app
  // does not discard it. Closing the tab does (session storage ends with
  // the tab), so that case gets its own warning below. The names drawer and
  // the assign drawer keep nothing.
  const hasUnsaved = (settingsOpen && settingsDirty) || assigningDirty;
  const editorDirty = isDirty(editor);
  React.useEffect(() => {
    if (!editorDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [editorDirty]);
  // Every way out of the assign drawer asks before dropping picked
  // locations or a changed date, like the other two editors.
  const closeAssigning = () => {
    if (saving) return;
    if (assigningDirty && !window.confirm("Discard this assignment?")) return;
    setAssigning(null);
  };
  React.useEffect(() => {
    if (!onDirtyChange) return undefined;
    onDirtyChange("tip-pools", hasUnsaved);
    return () => onDirtyChange("tip-pools", false);
  }, [onDirtyChange, hasUnsaved]);
  React.useEffect(() => { writeDraft(companyId, editor); }, [companyId, editor]);

  // Set while one of this component's own writes is refreshing the page, so
  // the new policy version it wrote is not mistaken for someone else's edit.
  const savingEditor = React.useRef(false);
  const request = async (url, body, successMessage) => {
    setSaving(true);
    // Any write made here (save, archive, assign) refreshes the page; the
    // policy version that comes back is this component's own doing, not a
    // concurrent edit to discard an open draft over.
    savingEditor.current = true;
    toast.loading("Saving tip pools...", { autoClose: false });
    try {
      await postRightworkAPI(url, { company_id: companyId, ...body });
      toast.dismiss();
      toast.success(successMessage, { autoClose: 3000 });
      await onChanged?.();
      return true;
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message ?? "Request Failed", { autoClose: 5000 });
      return false;
    } finally {
      savingEditor.current = false;
      setSaving(false);
    }
  };

  const openCreate = () => setEditor(withSnapshot({ uuid: null, name: "", notes: "", pools: [newPool(0)] }));
  const openEdit = (policy) => setEditor(withSnapshot({ uuid: policy.uuid, serverUpdatedAt: policy.updated_at || null, name: policy.name, notes: policy.notes || "", pools: policy.pools.map(poolToEditor) }));
  // A draft restored from storage was cut from a particular version of the
  // policy. If someone else saved it since, posting the draft would replace
  // their work (save sends the whole graph), so the draft is dropped instead.
  React.useEffect(() => {
    if (!editor?.uuid || !payload?.policies || savingEditor.current) return;
    const current = (payload.policies || []).find((policy) => policy.uuid === editor.uuid);
    if (current && (current.updated_at || null) === (editor.serverUpdatedAt || null)) return;
    setEditor(null);
    toast.error(current ? `${current.name} changed since your draft was started, so the draft was discarded. Open it again to edit the latest version.` : "The policy in your draft no longer exists, so the draft was discarded.", { autoClose: 6000 });
  }, [payload]);  // eslint-disable-line react-hooks/exhaustive-deps
  const closeEditor = () => setEditor(null);
  const dismissEditor = () => {
    if (saving) return;
    if (isDirty(editor) && !window.confirm("Discard unsaved changes to this policy?")) return;
    closeEditor();
  };
  const saveEditor = async () => {
    if (!editor) return;
    savingEditor.current = true;
    try {
      const ok = await request(API_save_tip_policy, { policy: editorToPayload(editor) }, "Policy saved");
      if (ok) closeEditor();
    } finally {
      savingEditor.current = false;
    }
  };
  const confirmArchive = async () => {
    const policy = pendingArchive;
    setPendingArchive(null);
    // No end date: the server ends each location on its OWN business date.
    const ok = await request(API_archive_tip_policy, { policy_uuid: policy.uuid }, `${policy.name} archived`);
    if (ok && editor?.uuid === policy.uuid) closeEditor();
  };
  const saveAssignment = async () => {
    if (!assigning) return;
    const ok = await request(
      API_assign_tip_policy_stores,
      { policy_uuid: assigning.policy_uuid, store_ids: assigning.store_ids, effective_start: assigning.effective_start },
      "Locations assigned"
    );
    if (ok) setAssigning(null);
  };
  const confirmUnassign = async () => {
    const store = pendingUnassign;
    setPendingUnassign(null);
    if (store.assignment_uuid) {
      // Only the scheduled move goes; whatever policy runs today keeps running.
      await request(API_unassign_tip_policy_store, { store_id: store.id, assignment_uuid: store.assignment_uuid }, `${store.store_name} will stay on its current policy`);
      return;
    }
    // No date: the server ends it on the location's own business date. The
    // policy shown is sent too, so a row gone stale under another admin's
    // move is refused rather than ending the policy they just chose.
    await request(API_unassign_tip_policy_store, { store_id: store.id, policy_uuid: store.policy_uuid }, `${store.store_name} removed from its policy`);
  };
  const openSettings = () => {
    setSettingsPicked((payload?.service_charge_checklist || []).filter((row) => row.saved).map((row) => ({ key: row.key, name: row.name })));
    setCustomName("");
    setSettingsOpen(true);
  };
  // Every way out of the names drawer (Cancel, the header close, Escape, the
  // backdrop) asks before discarding edits, like leaving the page does.
  const closeSettings = () => {
    if (saving) return;
    if (settingsDirty && !window.confirm("Discard unsaved changes to the service charge names?")) return;
    setSettingsOpen(false);
  };
  const saveSettings = async () => {
    const names = namesToSave.map((entry) => entry.name);
    // The version the drawer opened from: the server refuses to replace a
    // newer list, so two admins cannot silently undo each other.
    const ok = await request(API_save_tip_settings, {
      gratuity_service_charge_names: names,
      expected_updated_at: payload?.settings?.updated_at || "new",
    }, "Service charge names saved");
    if (ok) setSettingsOpen(false);
  };

  const updatePool = (index, patch) =>
    setEditor((prev) => ({ ...prev, pools: prev.pools.map((pool, i) => (i === index ? { ...pool, ...patch } : pool)) }));
  const updateWindow = (index, day, patch) =>
    updatePool(index, { windows: editor.pools[index].windows.map((row) => (row.day_of_week === day ? { ...row, ...patch } : row)) });
  const toggleSource = (index, source) => {
    const current = editor.pools[index].sources;
    updatePool(index, { sources: current.includes(source) ? current.filter((s) => s !== source) : [...current, source] });
  };
  const toggleRole = (index, roleId) => {
    const points = { ...editor.pools[index].role_points };
    if (points[roleId] !== undefined) delete points[roleId];
    else points[roleId] = "1";
    updatePool(index, { role_points: points });
  };
  const setPoints = (index, roleId, value) => updatePool(index, { role_points: { ...editor.pools[index].role_points, [roleId]: value } });
  const addPool = () => setEditor((prev) => ({ ...prev, pools: [...prev.pools, newPool(prev.pools.length)] }));
  const removePool = (index) => setEditor((prev) => ({ ...prev, pools: prev.pools.filter((_, i) => i !== index) }));

  if (!isAdmin) {
    return <p className="mx-6 py-5 text-sm text-slate-500 sm:mx-3">Only company administrators can manage tip pools.</p>;
  }
  if (payload?.error) {
    return (
      <div className="px-8 py-5 sm:px-4">
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <div className="text-sm font-semibold text-slate-700">Tip pools could not be loaded</div>
          <button type="button" onClick={() => onChanged?.()} className="mt-2 text-sm font-semibold text-violet-700 hover:text-violet-800">Try again</button>
        </div>
      </div>
    );
  }
  if (!payload) {
    return <div className="flex justify-center py-24"><LoadSpinner /></div>;
  }

  const chip = (text, key) => (
    <span key={key || text} className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">{text}</span>
  );
  const tag = (text, tone = "violet") => (
    <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${tone === "violet" ? "bg-violet-50 text-violet-700" : tone === "green" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{text}</span>
  );

  const policyListRow = (policy, index) => {
    const isSelected = selected?.uuid === policy.uuid;
    const count = storesOn(policy.uuid).length;
    const cadences = [...new Set(policy.pools.map((pool) => CADENCES.find((c) => c.value === pool.cadence)?.label || pool.cadence))].join(" / ");
    // The chip shows the policy's own letter when its name starts with one
    // ("G - Tavolata Boise" is G even when it sits sixth), else its position.
    const policyLetter = (row, position) => (/^[A-Z](?=\s*[-:.])/.exec(row.name || "") || [String.fromCharCode(65 + (position % 26))])[0];
    return (
      <button
        key={policy.uuid}
        type="button"
        onClick={() => setSelectedUuid(policy.uuid)}
        aria-pressed={isSelected}
        className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50 ${isSelected ? "bg-violet-50" : ""}`}
      >
        <span className={`rounded px-1.5 py-px text-[11px] font-semibold uppercase tracking-wider ${isSelected ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{policyLetter(policy, index)}</span>
        <span className="flex-1">
          <span className={`block text-sm font-medium ${isSelected ? "text-violet-800" : "text-slate-900"}`}>{policy.name}</span>
          <span className="block text-xs text-slate-500">{count} {count === 1 ? "location" : "locations"} · {policy.pools.length} {policy.pools.length === 1 ? "pool" : "pools"} · {cadences}</span>
        </span>
      </button>
    );
  };

  const poolCard = (pool) => {
    const points = (pool.role_points || []).filter((item) => Number(item.points) > 0).sort((a, b) => Number(b.points) - Number(a.points));
    // Against the pool's real highest weight (fractional weights included); 1 only guards an empty list.
    const maxPoints = Math.max(...points.map((item) => Number(item.points))) || 1;
    const wide = (selected?.pools || []).length === 1;
    return (
      <div key={pool.uuid} className="rounded-lg border border-slate-200 p-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{pool.name}</span>
          {pool.split_group && tag(`${String(pool.share_percent).replace(/\.00$/, "")}%`, "slate")}
          {chip(CADENCES.find((c) => c.value === pool.cadence)?.label || pool.cadence, "cadence")}
        </div>
        <p className="mt-1.5 text-xs text-slate-500">{windowSummary(pool)}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">{(pool.sources || []).map((source) => tag(SOURCE_SHORT[source] || source))}</div>
        {(pool.excluded_dining_options || []).length > 0 && (
          <p className="mt-2 text-xs text-slate-500">Excludes: {pool.excluded_dining_options.join(", ")}</p>
        )}
        {/* Each job code's points as a bar, exactly proportional to the
            pool's highest (the number beside it carries the value when the
            bar is too thin to see); columns when the card has the whole row
            to itself, so the number stays near the name. */}
        <ul className={`mt-2 text-[13px] ${wide ? "grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-x-6" : ""}`} aria-label={`${pool.name} job codes and points`}>
          {points.map((item) => (
            <li key={item.role_id} className="relative flex items-center justify-between border-t border-slate-100 py-1.5">
              <span aria-hidden="true" className="absolute inset-y-1 left-0 rounded-sm bg-violet-50" style={{ width: `${(Number(item.points) / maxPoints) * 100}%` }} />
              <span className="relative pl-2 text-slate-700">{roleName(item.role_id)}</span>
              <span className="relative pr-1 font-semibold text-slate-900">{String(item.points).replace(/\.00$/, "")}</span>
            </li>
          ))}
          {points.length === 0 && <li className="py-1.5 text-xs text-amber-700">No job codes have points; this pool cannot pay anyone.</li>}
        </ul>
      </div>
    );
  };

  const uncovered = (selected?.uncovered_mapped_role_ids || []).map((id) => roles.find((role) => role.id === id)).filter(Boolean);

  return (
    <div className="px-8 py-5 sm:px-4">
      {payload.refresh_failed && (
        <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>Your change was saved, but the page could not be refreshed; this is the last loaded data.</span>
          <button type="button" onClick={() => onChanged?.()} className="font-semibold text-violet-700 hover:text-violet-800">Refresh</button>
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">A policy describes the pools, cadence, sources, and points once. Assign it to every location that follows it.</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openSettings} className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Service charge names</button>
          <button type="button" onClick={openCreate} className="flex items-center gap-1 rounded-lg bg-violet-700 px-3 py-2 text-sm font-medium text-white hover:bg-violet-800"><AddIcon fontSize="small" /> New policy</button>
        </div>
      </div>

      {(payload.settings?.gratuity_service_charge_names || []).length === 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800">
          <WarningAmberOutlinedIcon style={{ fontSize: 16 }} />
          <span>No service charge names are listed. Only charges Toast flags as gratuity will count as tip money until you add them.</span>
          <button type="button" onClick={openSettings} className="ml-auto text-[13px] font-medium text-violet-700 hover:underline">Add names</button>
        </div>
      )}

      <div className="grid grid-cols-[320px_minmax(0,1fr)] gap-4 md:grid-cols-1">
        <section aria-label="Policies" className="self-start overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
            <span className="text-sm font-semibold text-slate-900">Policies</span>
            <span className="text-xs text-slate-500">{active.length} active · {stores.filter((s) => s.current_policy_uuid).length} locations assigned</span>
          </div>
          {active.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-500">No policies yet. Create one, then assign locations to it.</div>}
          {active.map(policyListRow)}
          {archived.length > 0 && (
            <div className="border-t border-slate-200">
              <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Archived</p>
              {archived.map((policy, index) => policyListRow(policy, active.length + index))}
            </div>
          )}
          {unassigned.length > 0 && (
            <div className="flex items-center gap-2.5 border-t border-slate-200 px-4 py-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />{unassigned.length} {unassigned.length === 1 ? "location has" : "locations have"} no policy
              </span>
            </div>
          )}
        </section>

        <div className="flex flex-col gap-3">
          {!selected && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">Select or create a policy to see its pools.</div>
          )}
          {selected && (
            <>
              <section aria-label={`${selected.name} policy`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <span className="text-sm font-semibold text-slate-900">{selected.name}</span>
                  {selected.status !== "active" && tag("Archived", "slate")}
                  {selected.pools.some((pool) => pool.split_group) && tag("Split pool")}
                  {selected.notes && <span className="text-xs text-slate-500">{selected.notes}</span>}
                  <span className="flex-1" />
                  {selected.status === "active" && <button type="button" onClick={() => openEdit(selected)} className="text-xs font-medium text-violet-700 hover:underline">Edit</button>}
                </div>
                {/* As many 280px-or-wider cards as the pane itself allows
                    (three daypart pools on a wide screen, fewer beside the
                    policy column, the docked assistant, or a phone width):
                    sized by the container, not the viewport. */}
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-3 p-4">
                  {selected.pools.map(poolCard)}
                </div>
              </section>

              <section aria-label="Locations on this policy" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <span className="text-sm font-semibold text-slate-900">Locations on this policy</span>
                  <span className="text-xs text-slate-500">{storesOn(selected.uuid).length} {storesOn(selected.uuid).length === 1 ? "location" : "locations"}</span>
                  <span className="flex-1" />
                  {selected.status === "active" && (
                    <button type="button" onClick={() => setAssigning({ policy_uuid: selected.uuid, policy_name: selected.name, store_ids: [], effective_start: todayIso(), initial_effective_start: todayIso() })} className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline">
                      <AddIcon style={{ fontSize: 14 }} />Assign locations
                    </button>
                  )}
                </div>
                {assignmentsOn(selected.uuid).length === 0 && <div className="px-4 py-4 text-sm text-slate-500">No locations yet.</div>}
                {assignmentsOn(selected.uuid).map((row) => {
                  const upcoming = isScheduled(row);
                  return (
                    <div key={row.uuid} className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 last:border-b-0">
                      <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                      <span className="text-sm font-medium text-slate-900">{row.store.store_name}</span>
                      {upcoming && tag("Upcoming", "slate")}
                      <span className="text-xs text-slate-500">{upcoming ? "from" : "since"} {row.effective_start}{row.effective_end ? ` until ${row.effective_end}` : ""}</span>
                      {!row.store.tip_pooling_enabled && <span className="text-xs text-amber-700">Tip pooling is off for this location</span>}
                      <span className="flex-1" />
                      {/* An archived policy's history is read-only; "Remove" here would only wipe the location's upcoming moves. */}
                      {selected.status !== "archived" && (
                        <button type="button" onClick={() => setPendingUnassign({ ...row.store, assignment_uuid: upcoming ? row.uuid : null, effective_start: row.effective_start, policy_uuid: selected.uuid })} className="text-xs font-medium text-slate-500 hover:text-rose-600 hover:underline">{upcoming ? "Cancel" : "Remove"}</button>
                      )}
                    </div>
                  );
                })}
              </section>

              {uncovered.length > 0 && (
                <section aria-label="Role coverage" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="text-sm font-semibold text-slate-900">Role coverage</span>
                    <span className="text-xs text-slate-500">POS job codes at this company that sit in none of this policy's pools</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 px-4 py-3">
                    {uncovered.map((role) => chip(role.role_name, role.id))}
                  </div>
                  <p className="px-4 pb-3 text-xs text-slate-500">Hours under these job codes earn nothing from the pool. That is right for training, meetings, and management; add points for anything else.</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      {/* Policy editor */}
      <Drawer open={Boolean(editor)} anchor="right" onClose={dismissEditor}>
        {editor && (
          <section aria-label={editor.uuid ? `Edit ${editor.name || "policy"}` : "New policy"} className="relative h-full w-[560px] max-w-full">
            <div className="max-h-screen overflow-auto">
              <DrawerHeader title={editor.uuid ? `Edit ${editor.name || "policy"}` : "New policy"} onClose={dismissEditor} />
              {/* Inert while a save is in flight: an edit typed after the
                  snapshot was posted would be lost when the drawer closes. */}
              <div className={`w-full px-6 ${saving ? "opacity-60" : ""}`} aria-busy={saving} {...(saving ? { inert: "" } : {})}>
                <div className="mt-2">
                  <p className="text-sm text-slate-500">Policy name</p>
                  <div className="mt-1">
                    <TextField fullWidth variant="outlined" size="small" inputProps={{ "aria-label": "Policy name" }} value={editor.name} onChange={(event) => setEditor((prev) => ({ ...prev, name: event.target.value }))} InputProps={{ sx: inputSx }} />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-slate-500">Notes</p>
                  <div className="mt-1">
                    <TextField fullWidth variant="outlined" size="small" inputProps={{ "aria-label": "Policy notes" }} value={editor.notes} onChange={(event) => setEditor((prev) => ({ ...prev, notes: event.target.value }))} InputProps={{ sx: inputSx }} />
                  </div>
                </div>

                {editor.pools.map((pool, index) => (
                  <div key={index} className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Pool {index + 1}</p>
                      <span className="flex-1" />
                      {editor.pools.length > 1 && (
                        <button type="button" onClick={() => removePool(index)} aria-label={`Remove pool ${index + 1}`} className="text-slate-400 hover:text-rose-600"><CloseIcon style={{ fontSize: 16 }} /></button>
                      )}
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-slate-500">Pool name</p>
                      <TextField fullWidth variant="outlined" size="small" inputProps={{ "aria-label": `Pool ${index + 1} name` }} value={pool.name} onChange={(event) => updatePool(index, { name: event.target.value })} InputProps={{ sx: inputSx }} />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label id={`pool-${index}-cadence-label`} htmlFor={`pool-${index}-cadence`} className="text-sm text-slate-500">Cadence</label>
                        <div className="mt-1">
                          <RightworkSelect id={`pool-${index}-cadence`} value={pool.cadence} onChange={(event) => updatePool(index, { cadence: event.target.value })} options={CADENCES} />
                        </div>
                      </div>
                      <div>
                        <label id={`pool-${index}-schedule-label`} htmlFor={`pool-${index}-schedule`} className="text-sm text-slate-500">Schedule</label>
                        <div className="mt-1">
                          <RightworkSelect id={`pool-${index}-schedule`} value={pool.always_active ? "always" : "windows"} onChange={(event) => updatePool(index, { always_active: event.target.value === "always" })} options={[{ value: "always", label: "Always active" }, { value: "windows", label: "Time windows" }]} />
                        </div>
                      </div>
                    </div>
                    {!pool.always_active && pool.windows_locked && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        This pool has more than one time window on the same day, which this editor cannot change yet. Its windows are kept exactly as they are when you save:
                        <ul className="mt-1 list-disc pl-4">
                          {(pool.raw_windows || []).map((window, i) => <li key={i}>{DAYS[window.day_of_week]} {window.start_time} to {window.end_time}</li>)}
                        </ul>
                      </div>
                    )}
                    {!pool.always_active && !pool.windows_locked && (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-white">
                        {pool.windows.map((row) => (
                          <div key={row.day_of_week} className="flex items-center gap-2 border-b border-slate-100 px-2 py-1 last:border-b-0">
                            <Checkbox checked={row.enabled} onChange={() => updateWindow(index, row.day_of_week, { enabled: !row.enabled })} size="small" inputProps={{ "aria-label": `Pool ${index + 1} ${DAYS[row.day_of_week]}` }} sx={checkboxSx} />
                            <span className="w-9 text-[13px] text-slate-700">{DAYS[row.day_of_week]}</span>
                            <RightworkTimePicker disabled={!row.enabled} value={row.start_time} label={`Pool ${index + 1} ${DAYS[row.day_of_week]} start`} onChange={(time) => updateWindow(index, row.day_of_week, { start_time: time })} />
                            <span className="text-xs text-slate-500">to</span>
                            <RightworkTimePicker disabled={!row.enabled} value={row.end_time} label={`Pool ${index + 1} ${DAYS[row.day_of_week]} end`} onChange={(time) => updateWindow(index, row.day_of_week, { end_time: time })} />
                          </div>
                        ))}
                        <p className="px-3 py-2 text-xs text-slate-500">Times are the location's local time; an end earlier than the start runs past midnight. Punches and payments are split at the edges.</p>
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="text-sm text-slate-500">Tip sources</p>
                      <div className="mt-1 rounded-lg border border-slate-200 bg-white">
                        {(payload.sources || []).map((source) => {
                          const checked = pool.sources.includes(source.value);
                          return (
                            <label key={source.value} className="flex min-h-[38px] cursor-pointer items-center gap-2 px-3 py-1 hover:bg-slate-50">
                              <Checkbox checked={checked} onChange={() => toggleSource(index, source.value)} size="small" inputProps={{ "aria-label": `Pool ${index + 1} ${source.label}` }} sx={checkboxSx} />
                              <span className={`text-sm ${checked ? "font-medium text-violet-800" : "text-slate-700"}`}>{source.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="mt-3">
                      <label id={`pool-${index}-excluded-label`} htmlFor={`pool-${index}-excluded`} className="text-sm text-slate-500">Exclude dining options</label>
                      <div className="mt-1">
                        <RightworkSelect
                          id={`pool-${index}-excluded`}
                          multiple
                          value={pool.excluded_dining_options}
                          renderValue={(values) => (values.length ? values.join(", ") : "None excluded")}
                          onChange={(event) => updatePool(index, { excluded_dining_options: event.target.value })}
                          options={[...new Set([...(payload.dining_options || []), ...pool.excluded_dining_options])].map((name) => ({ value: name, label: name }))}
                        />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Dining options seen on this company's Toast orders in the last 90 days. Third-party delivery tips belong here.</p>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-slate-500">Share of the money (%)</p>
                        <TextField fullWidth variant="outlined" size="small" inputProps={{ "aria-label": `Pool ${index + 1} share percent`, inputMode: "decimal" }} value={pool.share_percent} onChange={(event) => updatePool(index, { share_percent: event.target.value })} InputProps={{ sx: inputSx }} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Split group</p>
                        <TextField fullWidth variant="outlined" size="small" placeholder="e.g. house" inputProps={{ "aria-label": `Pool ${index + 1} split group` }} value={pool.split_group} onChange={(event) => updatePool(index, { split_group: event.target.value })} InputProps={{ sx: inputSx }} />
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Pools with the same split group divide the same money; their shares must add up to 100.</p>
                    <div className="mt-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-500">Job codes and points</p>
                        <span className="text-xs text-slate-500">{Object.keys(pool.role_points).length} of {roles.length} included</span>
                      </div>
                      <div className="mt-1 max-h-[280px] overflow-y-auto rounded-lg border border-slate-200 bg-white">
                        {roles.map((role) => {
                          const checked = pool.role_points[role.id] !== undefined;
                          return (
                            <div key={role.id} className="flex min-h-[40px] items-center gap-2 border-b border-slate-100 px-3 py-1 last:border-b-0 hover:bg-slate-50">
                              <Checkbox checked={checked} onChange={() => toggleRole(index, role.id)} size="small" inputProps={{ "aria-label": `Pool ${index + 1} ${role.role_name}` }} sx={checkboxSx} />
                              <span className={`flex-1 text-sm ${checked ? "font-medium text-violet-800" : "text-slate-700"}`}>{role.role_name}</span>
                              {!role.pos_mapped && <span className="text-[11px] text-slate-400">no POS job</span>}
                              {checked && (
                                <TextField size="small" inputProps={{ "aria-label": `Pool ${index + 1} ${role.role_name} points`, inputMode: "decimal", style: { width: 44, textAlign: "center" } }} value={pool.role_points[role.id]} onChange={(event) => setPoints(index, role.id, event.target.value)} InputProps={{ sx: smallInputSx }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addPool} className="mt-4 flex items-center gap-1 text-sm font-medium text-violet-700 hover:underline"><AddIcon style={{ fontSize: 16 }} />Add another pool</button>

                {editor.uuid && (
                  <div className="mt-6">
                    <button type="button" onClick={() => {
                      // Archiving closes the editor and drops its draft: unsaved edits are named before they go.
                      if (isDirty(editor) && !window.confirm("This policy has unsaved changes. Archive it and discard them?")) return;
                      setPendingArchive(policies.find((policy) => policy.uuid === editor.uuid));
                    }} className="text-sm font-medium text-rose-600 hover:underline">Archive policy</button>
                  </div>
                )}
              </div>
              <div className="h-[200px]" />
            </div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  disabled={!editor.name.trim() || editor.pools.length === 0}
                  onClick={saveEditor}
                  sx={{ width: "100%", textTransform: "none", borderRadius: "12px", paddingY: "14px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" }, fontSize: "16px" }}
                >
                  Save policy
                </LoadingButton>
              </div>
              <div className="mt-3 w-full">
                <ButtonOutlined text="Cancel" onClick={dismissEditor} />
              </div>
            </DrawerFooterContainerLarge>
          </section>
        )}
      </Drawer>

      {/* Assign locations */}
      <Drawer open={Boolean(assigning)} anchor="right" onClose={closeAssigning}>
        {assigning && (
          <section aria-label={`Assign locations to ${assigning.policy_name}`} className="relative h-full w-[560px] max-w-full">
            <div className="max-h-screen overflow-auto">
              <DrawerHeader title="Assign locations" onClose={closeAssigning} />
              <div className={`w-full px-6 ${saving ? "opacity-60" : ""}`} aria-busy={saving} {...(saving ? { inert: "" } : {})}>
                <p className="text-sm text-slate-500">Effective from</p>
                <div className="mt-1">
                  <RightworkDatePicker fullWidth height={45} value={assigning.effective_start} minDate={earliestStartForAll(assigning.store_ids)} label="Effective from"
                    onChange={(date) => setAssigning((prev) => ({ ...prev, effective_start: date }))} />
                </div>
                <p className="mt-1 text-xs text-slate-500">A location's current policy ends the day before, so a location already on a policy moves from today or later. A location with no policy can be backdated to compute past weeks, but not into dates an earlier policy already covered.</p>
                <div className="mt-5 flex items-center justify-between">
                  <p className="text-sm text-slate-500">Locations</p>
                  <span className="text-xs text-slate-500">{assigning.store_ids.length} of {assignable.length}</span>
                </div>
                <div className="mt-1 max-h-[420px] overflow-y-auto rounded-lg border border-slate-200">
                  {assignable.length === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-slate-500">
                      {!stores.some((store) => store.pos_provider === "toast")
                        ? "No Toast locations in this company."
                        : stores.some((store) => store.pos_provider === "toast" && store.tip_pooling_enabled)
                          ? "Every Toast location with tip pooling on needs an address with coordinates (Settings > Locations) before it can be assigned."
                          : "Tip pooling is switched off at every Toast location. Turn it on under Settings > Locations first."}
                    </div>
                  )}
                  {assignable.map((store) => {
                    const checked = assigning.store_ids.includes(store.id);
                    const upcoming = upcomingFor(store.id);
                    const currentAssignment = allAssignments().find((assignment) => assignment.store_id === store.id && assignment.status === "current");
                    const current = currentAssignment && currentAssignment.policy_uuid !== assigning.policy_uuid ? currentAssignment.policy : null;
                    // The location's final scheduled move; open-ended and to this
                    // policy, it already ends up here and the server would treat
                    // another assignment as a no-op rather than move the date.
                    const lastScheduled = allAssignments()
                      .filter((assignment) => assignment.store_id === store.id && isScheduled(assignment))
                      .sort((a, b) => b.effective_start.localeCompare(a.effective_start))[0];
                    const scheduledHere = Boolean(lastScheduled) && lastScheduled.policy_uuid === assigning.policy_uuid && !lastScheduled.effective_end;
                    const already = scheduledHere || (Boolean(currentAssignment) && currentAssignment.policy_uuid === assigning.policy_uuid && !upcoming);
                    return (
                      <label key={store.id} className="flex min-h-[42px] cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                        <Checkbox checked={checked || already} disabled={already} onChange={() => setAssigning((prev) => {
                          const store_ids = checked ? prev.store_ids.filter((id) => id !== store.id) : [...prev.store_ids, store.id];
                          // Adding a location with a later floor raises the date with it.
                          const floor = earliestStartForAll(store_ids);
                          const effective_start = floor && prev.effective_start < floor ? floor : prev.effective_start;
                          return { ...prev, store_ids, effective_start };
                        })} size="small" inputProps={{ "aria-label": store.store_name }} sx={checkboxSx} />
                        <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className={`text-sm ${checked || already ? "font-medium text-violet-800" : "text-slate-700"}`}>{store.store_name}</span>
                          <span className="text-[11px] text-slate-500">{already ? (scheduledHere && !(currentAssignment && currentAssignment.policy_uuid === assigning.policy_uuid) ? `Scheduled for this policy from ${lastScheduled.effective_start}` : "On this policy") : upcoming ? `Moves to ${upcoming.policy.name} on ${upcoming.effective_start}` : current ? `Moves from ${current.name}` : "No policy"}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {stores.length > assignable.length && (
                  (() => {
                    const notToast = stores.filter((store) => store.pos_provider !== "toast").length;
                    const switchedOff = stores.filter((store) => store.pos_provider === "toast" && !store.tip_pooling_enabled).length;
                    const noZone = stores.filter((store) => store.pos_provider === "toast" && store.tip_pooling_enabled && !zoneKnown(store));
                    return (
                      <p className="mt-2 text-xs text-slate-500">
                        {notToast > 0 && `${notToast} ${notToast === 1 ? "location is" : "locations are"} not on Toast and cannot pool tips yet.`}
                        {notToast > 0 && switchedOff > 0 && " "}
                        {switchedOff > 0 && `${switchedOff} Toast ${switchedOff === 1 ? "location has" : "locations have"} tip pooling switched off (Settings > Locations).`}
                        {(notToast > 0 || switchedOff > 0) && noZone.length > 0 && " "}
                        {noZone.length > 0 && `${noZone.map((store) => store.store_name).join(", ")} ${noZone.length === 1 ? "needs" : "need"} an address with coordinates (Settings > Locations) so the time zone is known.`}
                      </p>
                    );
                  })()
                )}
              </div>
              <div className="h-[200px]" />
            </div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton variant="contained" loading={saving} loadingPosition="end" endIcon={<SaveIcon />} disabled={assigning.store_ids.length === 0 || !assigning.effective_start || (earliestStartForAll(assigning.store_ids) && assigning.effective_start < earliestStartForAll(assigning.store_ids))} onClick={saveAssignment} sx={{ width: "100%", textTransform: "none", borderRadius: "12px", paddingY: "14px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" }, fontSize: "16px" }}>
                  Assign locations
                </LoadingButton>
              </div>
              <div className="mt-3 w-full">
                <ButtonOutlined text="Cancel" onClick={closeAssigning} />
              </div>
            </DrawerFooterContainerLarge>
          </section>
        )}
      </Drawer>

      {/* Service charge names */}
      <Drawer open={settingsOpen} anchor="right" onClose={closeSettings}>
        <section aria-label="Service charge names" className="relative h-full w-[450px] max-w-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader title="Service charge names" onClose={closeSettings} />
            {/* Inert while the save is in flight, like the policy editor: text
                typed after the snapshot was posted would be lost on close. */}
            <div className={`w-full px-6 ${saving ? "opacity-60" : ""}`} aria-busy={saving} {...(saving ? { inert: "" } : {})}>
              <p className="text-sm text-slate-600">Checked service charges count as gratuity in every pool that includes gratuity service charges. The list is every service charge Toast has sent for this company.</p>
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-slate-500">Service charges from Toast</p>
                <span className="text-xs text-slate-500">{settingsPicked.length} counted</span>
              </div>
              <div className="mt-1 rounded-lg border border-slate-200 bg-white">
                {nameRows.length === 0 && (
                  <p className="px-3 py-4 text-sm text-slate-500">No service charges have come through Toast yet. They appear here after the first rebuild at each location; a charge Toast has not sent yet can be added below.</p>
                )}
                {nameRows.map((row) => (
                  <label key={row.key} className="flex min-h-[42px] cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-1.5 last:border-b-0 hover:bg-slate-50">
                    <Checkbox checked={isPicked(row.key)} onChange={() => togglePicked(row)} size="small" inputProps={{ "aria-label": row.name }} sx={checkboxSx} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={`text-sm ${isPicked(row.key) ? "font-medium text-violet-800" : "text-slate-700"}`}>{row.name}</span>
                      <span className="text-[11px] text-slate-500">
                        {row.toast_gratuity && !row.seen_unflagged ? "Toast marks this as gratuity, so it counts either way"
                          : row.toast_gratuity ? "Toast marks this as gratuity at some locations only; check it so it counts everywhere"
                          : !row.seen ? "Not seen on a Toast check yet" : row.last_seen ? `Last seen ${row.last_seen}` : "Seen on Toast checks"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-500">Add a charge Toast has not sent yet</p>
              <div className="mt-1 flex items-center gap-2">
                <TextField fullWidth size="small" variant="outlined" inputProps={{ "aria-label": "Service charge name to add" }} value={customName} placeholder="Exactly as it is named in Toast" onChange={(event) => setCustomName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addCustomName(); } }} InputProps={{ sx: inputSx }} />
                <button type="button" onClick={addCustomName} disabled={!customName.trim()} className="flex items-center gap-1 rounded-full border border-violet-700 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400">
                  <AddIcon style={{ fontSize: 16 }} /> Add
                </button>
              </div>
            </div>
            <div className="h-[200px]" />
          </div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton variant="contained" loading={saving} loadingPosition="end" endIcon={<SaveIcon />} onClick={saveSettings} sx={{ width: "100%", textTransform: "none", borderRadius: "12px", paddingY: "14px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" }, fontSize: "16px" }}>
                Save names
              </LoadingButton>
            </div>
            <div className="mt-3 w-full">
              <ButtonOutlined text="Cancel" onClick={closeSettings} />
            </div>
          </DrawerFooterContainerLarge>
        </section>
      </Drawer>

      <ConfirmDialog
        open={Boolean(pendingArchive)}
        title={pendingArchive ? `Archive ${pendingArchive.name}?` : ""}
        message={pendingArchive ? `Its locations come off the policy today. Weeks already computed keep their runs.` : ""}
        confirmLabel="Archive"
        onConfirm={confirmArchive}
        onCancel={() => setPendingArchive(null)}
      />
      <ConfirmDialog
        open={Boolean(pendingUnassign)}
        title={pendingUnassign ? (pendingUnassign.assignment_uuid ? `Cancel the move of ${pendingUnassign.store_name}?` : `Remove ${pendingUnassign.store_name} from its policy?`) : ""}
        message={pendingUnassign?.assignment_uuid
          ? `The move scheduled for ${pendingUnassign.effective_start} is cancelled. The policy in force today keeps running.`
          : "Any assignment that has not started yet is cancelled and the current one ends today. Weeks already computed keep their runs; from tomorrow no tips are pooled at this location until it is assigned again."}
        confirmLabel={pendingUnassign?.assignment_uuid ? "Cancel move" : "Remove"}
        onConfirm={confirmUnassign}
        onCancel={() => setPendingUnassign(null)}
      />
    </div>
  );
}
